import React, { useEffect } from 'react';

import { Box, Button, Divider, Stack } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    ReactHookFormInput,
    ReactHookFormSelect,
} from 'common/components/form';
import yupResolver from 'common/utils/yupResolver';

import { ROLE_LEVELS } from '../../companyRoles/constants';
import * as actions from '../actions';
import * as selectors from '../selectors';
import validate from '../validate';

const FormContainer = ({
    companyId,
    filterData,
    financierGroupType,
    groups,
    handleClickCloseForm,
    handleClickResetPassword,
    initialValues,
    isNewRecord,
    isSaving,
    onChangePassword,
    onChangePasswordConfirmation,
    save,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, watch } = useForm({
        defaultValues: initialValues, // Usar initialValues como valores por defecto
        resolver: yupResolver(validate),
        context: { isNewRecord }, // Pasamos isNewRecord como contexto
    });

    // Reinicializar el formulario cuando cambien los initialValues
    useEffect(() => reset(initialValues), [initialValues, reset]);

    const formValues = watch();

    const groupsOptions = groups
        .filter(
            ({ financier_group_type }) =>
                financier_group_type?.level !== ROLE_LEVELS.OWNER,
        )
        .map((group) => ({
            label: group.name,
            value: group.id,
        }));
    groupsOptions.unshift({ value: '', label: t('Select a role') });

    const onSubmit = (values) => {
        save(filterData, { ...values, financier: companyId });
    };

    return (
        <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
            <ReactHookFormSelect
                control={control}
                label={`${t('Role')} *`}
                name="financier_group"
                options={groupsOptions}
            />

            <ReactHookFormInput
                control={control}
                label={`${t('Name')} *`}
                name="first_name"
                placeholder={t('Enter name')}
            />

            <ReactHookFormInput
                control={control}
                label={`${t('Last name')} *`}
                name="last_name"
                placeholder={t('Enter last name')}
            />

            <ReactHookFormInput
                control={control}
                label={`${t('Second last name')}`}
                name="second_surname"
                placeholder={t('Enter last name')}
            />

            <ReactHookFormInput
                control={control}
                disabled={!isNewRecord}
                label={`${t('Email')} *`}
                name="email"
                placeholder={t('Enter email')}
            />

            <ReactHookFormInput
                control={control}
                label={t('Phone')}
                name="phone"
                placeholder={t('Enter phone')}
            />

            {isNewRecord && (
                <>
                    <ReactHookFormInput
                        control={control}
                        label={`${t('Password')} *`}
                        name="password"
                        onChange={(e) => onChangePassword(e)}
                        placeholder={t('Enter password')}
                        type="password"
                    />

                    <ReactHookFormInput
                        control={control}
                        label={`${t('Confirm password')} *`}
                        name="confirm_password"
                        onChange={(e) =>
                            onChangePasswordConfirmation(e, formValues.password)
                        }
                        onFocus={(e) =>
                            onChangePasswordConfirmation(e, formValues.password)
                        }
                        placeholder={t('Enter password again')}
                        type="password"
                    />
                </>
            )}

            <Divider sx={{ mx: (theme) => `${theme.spacing(-2)}!important` }} />

            <Box display="flex">
                <Button
                    onClick={() => handleClickResetPassword(formValues.id)}
                    variant="text"
                    visible={!isNewRecord && financierGroupType?.level === 0}
                >
                    {t('Restore password')}
                </Button>
                <Box display="flex" sx={{ ml: 'auto', gap: 1 }}>
                    <Button
                        color="secondary"
                        onClick={handleClickCloseForm}
                        variant="text"
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        disabled={isSaving}
                        type="submit"
                        variant="contained"
                    >
                        {t('Save')}
                    </Button>
                </Box>
            </Box>
        </Stack>
    );
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isNewRecord: selectors.getIsNewRecord,
    isSaving: selectors.getIsSavingItem,
});

const mapDispatchToProps = (dispatch) => ({
    save: (filterData, values) => dispatch(actions.save(filterData, values)),
});

FormContainer.propTypes = {
    companyId: PropTypes.string,
    filterData: PropTypes.object,
    financierGroupType: PropTypes.object,
    groups: PropTypes.array,
    handleClickCloseForm: PropTypes.func,
    handleClickResetPassword: PropTypes.func,
    initialValues: PropTypes.object,
    isNewRecord: PropTypes.bool,
    isSaving: PropTypes.bool,
    onChangePassword: PropTypes.func,
    onChangePasswordConfirmation: PropTypes.func,
    save: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
